import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Card,
    Input,
    message,
    Modal,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import AdminMyAppsAPI from '../api/adminMyapps';

const { Text, Paragraph } = Typography;

const STATUS_COLOR = {
    pending: 'orange',
    approved: 'green',
    rejected: 'red',
};

function formatDate(s) {
    if (!s) return '';
    try {
        return new Date(s).toLocaleString();
    } catch (e) {
        return s;
    }
}

export default function AdminMyApps() {
    const [status, setStatus] = useState('pending');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejecting, setRejecting] = useState(false);

    const refresh = useCallback(() => {
        setLoading(true);
        AdminMyAppsAPI.list(
            status,
            (res) => {
                setRows(res || []);
                setLoading(false);
            },
            (err) => {
                console.log(err);
                setLoading(false);
                message.error(err?.detail || 'Failed to load');
            }
        );
    }, [status]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const onApprove = (row) => {
        Modal.confirm({
            title: `Approve "${row.app_name}"?`,
            content: `Owner: ${row.username}. This will let the app exchange tickets via /serviceValidation.`,
            onOk: () => new Promise((resolve) => {
                AdminMyAppsAPI.approve(
                    row.app_id,
                    () => {
                        message.success('Approved');
                        refresh();
                        resolve();
                    },
                    (err) => {
                        message.error(err?.detail || 'Approve failed');
                        resolve();
                    }
                );
            }),
        });
    };

    const openReject = (row) => {
        setRejectTarget(row);
        setRejectReason('');
    };

    const submitReject = () => {
        const reason = rejectReason.trim();
        if (!reason) {
            message.error('Reason is required');
            return;
        }
        setRejecting(true);
        AdminMyAppsAPI.reject(
            rejectTarget.app_id,
            reason,
            () => {
                setRejecting(false);
                setRejectTarget(null);
                setRejectReason('');
                message.success('Rejected');
                refresh();
            },
            (err) => {
                setRejecting(false);
                message.error(err?.detail || 'Reject failed');
            }
        );
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'app_name',
            render: (v, row) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{v}</Text>
                    <Text type="secondary" copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {row.app_id}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Owner',
            dataIndex: 'username',
        },
        {
            title: 'Service URL(s)',
            dataIndex: 'service_url',
            render: (v) => (
                <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                    {(v || '').split('::').join('\n')}
                </div>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            ellipsis: true,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (s) => <Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag>,
        },
        {
            title: 'Submitted',
            dataIndex: 'create_time',
            render: formatDate,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, row) => (
                <Space wrap>
                    <Button
                        size="small"
                        type="primary"
                        disabled={row.status !== 'pending' && row.status !== 'rejected'}
                        onClick={() => onApprove(row)}
                    >
                        Approve
                    </Button>
                    <Button
                        size="small"
                        danger
                        disabled={row.status !== 'pending'}
                        onClick={() => openReject(row)}
                    >
                        Reject
                    </Button>
                </Space>
            ),
        },
    ];

    const expandedRowRender = (row) => (
        <div>
            <Paragraph style={{ marginBottom: 8 }}>
                <Text strong>Description: </Text>
                {row.description || <Text type="secondary">(none)</Text>}
            </Paragraph>
            <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                <Text strong>Notes: </Text>
                {row.notes || <Text type="secondary">(none)</Text>}
            </Paragraph>
        </div>
    );

    return (
        <Card
            title="Admin — App Registration Requests"
            extra={
                <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
                    Refresh
                </Button>
            }
        >
            <Tabs
                activeKey={status}
                onChange={setStatus}
                items={[
                    { key: 'pending', label: 'Pending' },
                    { key: 'approved', label: 'Approved' },
                    { key: 'rejected', label: 'Rejected' },
                    { key: 'all', label: 'All' },
                ]}
            />
            <Table
                rowKey="app_id"
                columns={columns}
                dataSource={rows}
                loading={loading}
                pagination={false}
                expandable={{ expandedRowRender }}
            />
            <Modal
                title={rejectTarget ? `Reject "${rejectTarget.app_name}"` : 'Reject'}
                open={!!rejectTarget}
                onOk={submitReject}
                onCancel={() => { setRejectTarget(null); setRejectReason(''); }}
                confirmLoading={rejecting}
                okButtonProps={{ danger: true }}
                okText="Reject"
            >
                <Paragraph>
                    Reason will be appended to the app's notes and shown to the owner.
                </Paragraph>
                <Input.TextArea
                    rows={4}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Reason for rejection..."
                />
            </Modal>
        </Card>
    );
}
